import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import './App.css';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';


function GenerationView() {
  const { testId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Get topic, language, and level from location state or use defaults
  const { topic: initialTopic, language: initialLanguage, level: initialLevel } = location.state || {};
  
  const [topic, setTopic] = useState(initialTopic || '');
  const [language, setLanguage] = useState(initialLanguage || '');
  const [level, setLevel] = useState(initialLevel || '');
  const [email, setEmail] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [, setDebugInfo] = useState({});
  const [, setCurrentStep] = useState('');
  const [currentTestId, setCurrentTestId] = useState(testId || null);
  const [testStatus, setTestStatus] = useState(null);
  const [progress, setProgress] = useState({});
  const [showVideoStatusChecker, setShowVideoStatusChecker] = useState(false);
  const [videoIdInput, setVideoIdInput] = useState('');
  const [lastStatusCallTime, setLastStatusCallTime] = useState(0);
  const [currentIllustration, setCurrentIllustration] = useState(1);
  const [showDetailedProgress, setShowDetailedProgress] = useState(false);

  const languages = [
    { code: 'nl', name: 'Nederlands' },
    { code: 'en', name: 'English' },
    { code: 'fr', name: 'Français' },
    { code: 'es', name: 'Español' }
  ];

  const levels = [
    'VMBO BB',
    'VMBO KB',
    'VMBO GT',
    'HAVO',
    'VWO'
  ];

  // Validation helpers
  const isValidEmail = (email) => {
    if (!email.trim()) return false;
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const isValidTopic = (topic) => {
    return topic.trim().length >= 5;
  };

  const isLanguageValid = language.trim() !== '';
  const isLevelValid = level.trim() !== '';

  // Helper function to check if status call is allowed (throttling)
  const canMakeStatusCall = () => {
    const now = Date.now();
    const timeSinceLastCall = now - lastStatusCallTime;
    const minInterval = 10000; // 10 seconds in milliseconds
    
    return timeSinceLastCall >= minInterval;
  };

  // Load existing test if testId is provided
  useEffect(() => {
    if (testId) {
      loadExistingTest();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [testId]);

  // Cycle through illustrations
  useEffect(() => {
    if (isGenerating || testId) {
      const interval = setInterval(() => {
        setCurrentIllustration((prev) => {
          if (prev >= 3) return 1;
          return prev + 1;
        });
      }, 3000); // Change every 3 seconds

      return () => clearInterval(interval);
    }
  }, [isGenerating, testId]);

  // Removed auto-start generation to prevent duplicates
  // Generation now only starts when user clicks the manual button

  const loadExistingTest = async () => {
    try {
      setCurrentStep('Bestaande test laden...');
      setCurrentTestId(testId);
      
      // Load initial status without auto-continuation
      await updateStatusAfterJob(testId, true);
      
      // Check if test is still in progress and has pending steps
      if (testStatus && testStatus.status === 'in_progress') {
        if (testStatus.current_step) {
          setCurrentStep(`Hervatten: ${testStatus.current_step.step_name.replace(/_/g, ' ')}`);
          setIsGenerating(true);
        } else if (testStatus.next_step && testStatus.next_step.status === 'pending') {
          setCurrentStep(`Hervatten: ${testStatus.next_step.step_name.replace(/_/g, ' ')}`);
          setIsGenerating(true);
          
          // Start job execution (this will call updateStatusAfterJob with shouldContinue=true)
          await executeJobsLoop(testId);
        }
      } else if (testStatus && testStatus.status === 'completed') {
        setCurrentStep('Test succesvol voltooid!');
      } else if (testStatus && testStatus.status === 'failed') {
        setCurrentStep('Test mislukt. Je kunt individuele stappen opnieuw proberen.');
      }
      
    } catch (error) {
      console.error('Error loading existing test:', error);
      setCurrentStep('Fout bij laden test. Nieuwe generatie starten...');
    }
  };

  // Update status after job execution - and continue if next step available
  const updateStatusAfterJob = async (testId, shouldContinue = true) => {
    // Check if we can make a status call (throttling)
    if (!canMakeStatusCall()) {
      console.log('Status oproep beperkt - te snel na laatste oproep');
      return;
    }

    try {
      // Update the last call time
      setLastStatusCallTime(Date.now());
      
      const response = await axios.get(`${API_BASE_URL}/status/${testId}`);
      const statusData = response.data;
      
      setTestStatus(statusData);
      setProgress(statusData.progress);
      
      // Update debug info with current data
      updateDebugInfoFromStatus(statusData);
      
      // Auto-execute if there's a pending step and no in-progress step
      if (shouldContinue && statusData.next_step && statusData.next_step.status === 'pending') {
        const hasInProgressStep = statusData.steps.some(step => step.status === 'in_progress');
        if (!hasInProgressStep) {
          // Automatically execute the next pending job
          await executeJobsLoop(testId);
        }
      }
      
      if (shouldContinue && statusData.current_step.status === 'in_progress') {
        // delay 10 seconds
        await new Promise(resolve => setTimeout(resolve, 10000));
        // then execute the next job
        await executeJobsLoop(testId);
      }
      
    } catch (error) {
      console.error('Error updating status:', error);
    }
  };

  // Update debug info from database status
  const updateDebugInfoFromStatus = (statusData) => {
    const newDebugInfo = {};
    
    // Add safety check for statusData
    if (!statusData || !statusData.questions) {
      console.warn('Invalid statusData received:', statusData);
      return;
    }
    
    // Add strategy if available
    if (statusData.test?.strategy_data) {
      newDebugInfo.strategy = statusData.test.strategy_data;
    }
    
    // Helper function to safely parse options
    const parseOptions = (options) => {
      if (!options) return ["Option A", "Option B", "Option C", "Option D"];
      if (Array.isArray(options)) {
        return options.filter(opt => opt && typeof opt === 'string').length > 0 
          ? options.filter(opt => opt && typeof opt === 'string')
          : ["Option A", "Option B", "Option C", "Option D"];
      }
      if (typeof options === 'string') {
        try {
          const parsed = JSON.parse(options);
          if (Array.isArray(parsed)) {
            return parsed.filter(opt => opt && typeof opt === 'string').length > 0 
              ? parsed.filter(opt => opt && typeof opt === 'string')
              : ["Option A", "Option B", "Option C", "Option D"];
          }
          return ["Option A", "Option B", "Option C", "Option D"];
        } catch (e) {
          console.warn('Failed to parse options JSON:', e);
          return ["Option A", "Option B", "Option C", "Option D"];
        }
      }
      return ["Option A", "Option B", "Option C", "Option D"];
    };
    
    // Add questions data
    if (Array.isArray(statusData.questions)) {
      statusData.questions.forEach(question => {
        if (question.question_number === 1 && question.question_type === 'audio') {
          newDebugInfo.audioContent = {
            script: question.script,
            question: question.question_text,
            options: parseOptions(question.options),
            correct_answer: question.correct_answer,
            explanation: question.explanation,
            question_type: question.question_type_description,
            story_type: question.generation_metadata?.story_type,
            emotional_tone: question.generation_metadata?.emotional_tone
          };
          
          if (question.audio_url) {
            newDebugInfo.audio = {
              audio_file: question.audio_file_path,
              audio_url: question.audio_url,
              s3_url: question.audio_url.startsWith('http') ? question.audio_url : null,
              duration: "30 seconds",
              script: question.script,
              status: "success"
            };
          }
        } else if (question.question_number === 2 && question.question_type === 'video') {
          newDebugInfo.videoContent = {
            script: question.script,
            question: question.question_text,
            options: parseOptions(question.options),
            correct_answer: question.correct_answer,
            explanation: question.explanation,
            question_type: question.question_type_description,
            visual_elements: question.generation_metadata?.visual_elements,
            presentation_style: question.generation_metadata?.presentation_style,
            visual_theme: question.generation_metadata?.visual_theme,
            image_urls: question.images?.map(img => img.image_url) || [],
            image_paths: question.images?.map(img => img.image_path) || []
          };
          
          if (question.final_video_url) {
            newDebugInfo.final_video = {
              final_video: question.final_video_path,
              video_url: question.final_video_url,
              s3_url: question.final_video_url.startsWith('http') ? question.final_video_url : null,
              duration: "30.0 seconds",
              avatar_position: "bottom_right",
              slideshow_images: question.images?.length || 0,
              status: "success",
              message: "Final video created successfully"
            };
          }
        } else if (question.question_number === 3 && question.question_type === 'reading') {
          newDebugInfo.readingContent = {
            text: question.script, // Reading text is stored in script field
            question: question.question_text,
            options: parseOptions(question.options),
            correct_answer: question.correct_answer,
            explanation: question.explanation,
            question_type: question.question_type_description,
            text_type: question.generation_metadata?.text_type,
            reading_level: question.generation_metadata?.reading_level,
            key_points: question.generation_metadata?.key_points,
            image_urls: question.images?.map(img => img.image_url) || [],
            image_paths: question.images?.map(img => img.image_path) || []
          };
        }
      });
    }
    
    // Add HeyGen video data
    if (statusData.heygen_videos && statusData.heygen_videos.length > 0) {
      const heygenVideo = statusData.heygen_videos[0];
      newDebugInfo.avatar_video = {
        video_id: heygenVideo.heygen_video_id,
        status: heygenVideo.status,
        video_url: heygenVideo.video_url,
        thumbnail_url: heygenVideo.thumbnail_url,
        script: heygenVideo.script
      };
    }
    
    setDebugInfo(prev => ({ ...prev, ...newDebugInfo }));
  };


  // Manual video status checker
  const checkVideoStatusManually = async () => {
    if (!videoIdInput.trim()) {
      alert('Voer een video ID in!');
      return;
    }

    try {
      setCurrentStep('Video status controleren...');
      const statusResponse = await axios.get(`${API_BASE_URL}/heygen/video-status/${videoIdInput.trim()}`);
      const statusData = statusResponse.data;
      
      if (statusData.status === 'completed') {
        setCurrentStep('Video voltooid! Downloaden...');
        // Download the completed video
        const downloadResponse = await axios.post(`${API_BASE_URL}/heygen/download-video/${videoIdInput.trim()}`, {
          topic: topic || 'Onbekend onderwerp'
        });
        
        setDebugInfo(prev => ({ 
          ...prev, 
          avatar_video: { 
            ...prev.avatar_video, 
            ...downloadResponse.data 
          } 
        }));
        setCurrentStep('Avatar video ready!');
        setShowVideoStatusChecker(false);
        setVideoIdInput('');
        alert('✅ Video succesvol geladen!');
      } else if (statusData.status === 'failed') {
        setCurrentStep('Video generation failed');
        alert('❌ Video generatie mislukt!');
      } else {
        setCurrentStep(`Video is ${statusData.status}. Starting automatic polling...`);
        // Start polling for completion
        pollExternalJob(currentTestId, {
          external_type: 'heygen_video',
          external_id: videoIdInput.trim()
        });
        setShowVideoStatusChecker(false);
        setVideoIdInput('');
      }
    } catch (error) {
      console.error('Error checking video status manually:', error);
      setCurrentStep('Error checking video status');
      alert('❌ Fout bij het controleren van video status! Controleer of het video ID correct is.');
    }
  };

  const handleGenerate = async () => {
    if (!topic.trim()) {
      alert('Voer een onderwerp in!');
      return;
    }

    if (!isValidEmail(email)) {
      alert('Voer een geldig e-mailadres in!');
      return;
    }

    setIsGenerating(true);
    setDebugInfo({});
    setCurrentStep('Generatie starten...');
    setCurrentTestId(null);

    try {
      // Step 1: Create exam
      setCurrentStep('Examen aanmaken...');
      const createResponse = await axios.post(`${API_BASE_URL}/create-exam`, {
        topic,
        language,
        level,
        email: email.trim()
      });
      
      const testId = createResponse.data.test_id;
      setCurrentTestId(testId);
      
      // Start the job execution loop
      await executeJobsLoop(testId);
      
    } catch (error) {
      console.error('Error generating luistertoets:', error);
      setCurrentStep('Fout opgetreden tijdens generatie');
      setIsGenerating(false);
    }
  };

  // Simplified job execution loop - execute one job at a time
  const executeJobsLoop = async (testId) => {
    try {
      // Execute next job
      const jobResponse = await axios.post(`${API_BASE_URL}/execute-job/${testId}`);
      const jobData = jobResponse.data;
      
      // Update debug info with job data if available
      if (jobData.data) {
        setDebugInfo(prev => ({ ...prev, ...jobData.data }));
      }
      
      if (jobData.status === 'completed') {
        if (jobData.next_job) {
          setCurrentStep(`Voltooid: ${jobData.next_job.replace(/_/g, ' ')}`);
        } else {
          // All jobs completed
          setCurrentStep('Generatie voltooid!');
          setIsGenerating(false);
        }
      } else if (jobData.status === 'failed') {
        setCurrentStep(`Mislukt: ${jobData.message}`);
        setIsGenerating(false);
        alert(`❌ Generatie mislukt: ${jobData.message}`);
      } else if (jobData.status === 'in_progress') {
        // For jobs that need external polling (like HeyGen)
        setCurrentStep(`Bezig: ${jobData.message}`);
        
        // If it's a HeyGen video, start polling
        if (jobData.external_type === 'heygen_video') {
          await pollExternalJob(testId, jobData);
        } else {
          // For other waiting jobs, just wait and check status again
          setTimeout(async () => {
            await updateStatusAfterJob(testId);
          }, 5000); // Check again in 5 seconds
        }
      }
      
      // Update status after job execution (this will continue if next step available)
      await updateStatusAfterJob(testId);
      
    } catch (error) {
      console.error('Error in job execution:', error);
      setCurrentStep('Fout opgetreden tijdens job uitvoering');
      setIsGenerating(false);
    }
  };

  // Handle external job polling (like HeyGen videos)
  const pollExternalJob = async (testId, jobData) => {
    if (jobData.external_type === 'heygen_video') {
      const videoId = jobData.external_id;
      setCurrentStep('HeyGen video generatie gestart! Status controleren...');
      
      // Poll HeyGen video status
      const maxAttempts = 30; // 5 minutes with 10-second intervals
      let attempts = 0;
      
      const checkStatus = async () => {
        try {
          attempts++;
          setCurrentStep(`Video status controleren... (${attempts}/${maxAttempts})`);
          
          const statusResponse = await axios.get(`${API_BASE_URL}/heygen/video-status/${videoId}`);
          const statusData = statusResponse.data;
          
          if (statusData.status === 'completed') {
            setCurrentStep('Video voltooid! Downloaden...');
            // Download the completed video
            const downloadResponse = await axios.post(`${API_BASE_URL}/heygen/download-video/${videoId}`, {
              topic: topic
            });
            
            setDebugInfo(prev => ({ 
              ...prev, 
              avatar_video: { 
                ...prev.avatar_video, 
                ...downloadResponse.data 
              } 
            }));
            
            setCurrentStep('Avatar video klaar!');
            return; // Exit polling loop
          } else if (statusData.status === 'failed') {
            setCurrentStep('Video generatie mislukt');
            alert('❌ Video generatie mislukt!');
            return;
          } else if (attempts >= maxAttempts) {
            setCurrentStep('Video generatie timeout');
            alert('⏰ Video generatie duurt te lang. Probeer het later opnieuw.');
            return;
          } else {
            // Still processing, check again in 10 seconds
            setTimeout(checkStatus, 10000);
          }
        } catch (error) {
          console.error('Error checking video status:', error);
          setCurrentStep('Fout bij controleren video status');
          alert('❌ Fout bij het controleren van video status!');
        }
      };
      
      checkStatus();
    }
  };

  return (
    <div className="App">
      <header className="App-header">
        <div className="header-content">
          <button 
            onClick={() => navigate('/')}
            className="back-button"
          >
            ← Terug naar Homepage
          </button>
          <div className="generation-header">
            <h1>Luistertoets Generator</h1>
            <p>Genereer automatisch luistertoetsen over elk onderwerp</p>
          </div>
        </div>
      </header>

      <main className="App-main">
        {/* Input Section - Only show if not resuming existing test and not generating */}
        {!testId && !isGenerating && (
          <div className="input-section">
            {/* Language and Level dropdowns - side by side */}
            <div className="input-row">
              <div className={`input-group ${isLanguageValid ? 'valid' : ''}`}>
                <label htmlFor="language">Taal:</label>
                <div className="input-wrapper">
                  <select
                    id="language"
                    value={language}
                    onChange={(e) => setLanguage(e.target.value)}
                    disabled={isGenerating}
                  >
                    <option value="">Selecteer een taal</option>
                    {languages.map(lang => (
                      <option key={lang.code} value={lang.code}>
                        {lang.name}
                      </option>
                    ))}
                  </select>
                  {isLanguageValid && <span className="checkmark">✓</span>}
                </div>
              </div>

              <div className={`input-group ${isLevelValid ? 'valid' : ''}`}>
                <label htmlFor="level">Niveau:</label>
                <div className="input-wrapper">
                  <select
                    id="level"
                    value={level}
                    onChange={(e) => setLevel(e.target.value)}
                    disabled={isGenerating}
                  >
                    <option value="">Selecteer een niveau</option>
                    {levels.map(levelOption => (
                      <option key={levelOption} value={levelOption}>
                        {levelOption}
                      </option>
                    ))}
                  </select>
                  {isLevelValid && <span className="checkmark">✓</span>}
                </div>
              </div>
            </div>

            {/* Topic input */}
            <div className={`input-group ${isValidTopic(topic) ? 'valid' : ''}`}>
              <label htmlFor="topic">Onderwerp:</label>
              <div className="input-wrapper">
                <input
                  id="topic"
                  type="text"
                  value={topic}
                  onChange={(e) => setTopic(e.target.value)}
                  placeholder="Bijv: Socialmedia gebruik bij Jongeren"
                  disabled={isGenerating}
                />
                {isValidTopic(topic) && <span className="checkmark">✓</span>}
              </div>
            </div>

            {/* Email input */}
            <div className={`input-group ${isValidEmail(email) ? 'valid' : ''}`}>
              <label htmlFor="email">E-mailadres:</label>
              <div className="input-wrapper">
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="voorbeeld@email.com"
                  disabled={isGenerating}
                  required
                />
                {isValidEmail(email) && <span className="checkmark">✓</span>}
              </div>
              <p style={{ marginTop: '0.5rem', fontSize: '0.85rem', color: '#6c757d', fontStyle: 'italic' }}>
                Ontvang een melding per e-mail wanneer je toets klaar is (generatie kan tot 10 minuten duren)
              </p>
            </div>

            {/* Generate button - full width at bottom */}
            <div className="button-group-full">
              <button 
                onClick={handleGenerate} 
                disabled={isGenerating || !topic.trim() || !language.trim() || !level.trim() || !isValidEmail(email)}
                className="generate-button"
              >
                {isGenerating ? 'Genereren...' : 'Genereer Luistertoets'}
              </button>
            </div>
          </div>
        )}

        {/* Progress Section - Show when generating OR when testId exists (existing test) */}
        {(isGenerating || testId) && (
          <div className="progress-section">
            {/* Illustration above status */}
            <div className="illustration-container">
              <img 
                src={`/illustrations/illustration_${currentIllustration}.svg`} 
                alt={`Illustratie ${currentIllustration}`}
                className="progress-illustration"
              />
            </div>
            
            <h3>We zijn bezig met de generatie van jouw taaltoets</h3>
            <div className="bg-yellow-100 p-2 rounded-md text-center">
              Dit kan tot 10 minuten duren. Wacht even en check de status opnieuw. Je ontvangt een mailtje zodra je toets klaar is.
            </div>
            {progress && (
              <div className="progress-details">
                <p>Voortgang: {progress.completed_steps || 0} / {progress.total_steps || 0} stappen ({progress.progress_percentage || 0}%)</p>
                {progress.failed_steps > 0 && (
                  <p className="error-text">Mislukte stappen: {progress.failed_steps}</p>
                )}
              </div>
            )}
            <div className="progress-bar">
              <div 
                className="progress-fill" 
                style={{ 
                  width: `${progress.progress_percentage || 0}%`,
                  transition: 'width 0.3s ease'
                }}
              ></div>
            </div>
            
            {/* Detailed progress - collapsible */}
            {testStatus && testStatus.steps && (
              <div className="detailed-progress-section">
                <button 
                  className="toggle-detailed-progress"
                  onClick={() => setShowDetailedProgress(!showDetailedProgress)}
                >
                  {showDetailedProgress ? '▼' : '▶'} Bekijk gedetailleerde voortgang
                </button>
                {showDetailedProgress && (
                  <div className="steps-list">
                    {testStatus.steps.map((step, index) => (
                      <div key={index} className={`step-item ${step.status}`}>
                        <span className="step-name">{step.step_name.replace(/_/g, ' ')}</span>
                        <span className="step-status">{step.status}</span>
                        {step.duration_seconds && (
                          <span className="step-duration">({step.duration_seconds}s)</span>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Video Status Checker */}
        {showVideoStatusChecker && (
          <div className="video-status-section">
            <h3>🎬 Video Status Checker</h3>
            <p>Voer een video ID in om de status te controleren en het proces voort te zetten.</p>
            <div className="video-status-input">
              <input
                type="text"
                value={videoIdInput}
                onChange={(e) => setVideoIdInput(e.target.value)}
                placeholder="Bijv: 123ff77f30c04c2ba04446b6fbb8ef2e"
                className="video-id-input"
              />
              <button 
                onClick={checkVideoStatusManually}
                className="check-status-button"
                disabled={!videoIdInput.trim() || isGenerating}
              >
                Status Controleren
              </button>
            </div>
            <div className="video-status-help">
              <p><strong>Wanneer gebruiken:</strong></p>
              <ul>
                <li>Als je al een video ID hebt van een eerdere generatie</li>
                <li>Als het generatieproces werd onderbroken</li>
                <li>Om de status van een lopende video te controleren</li>
              </ul>
            </div>
          </div>
        )}


        {/* Action Buttons - Removed bekijk test and video checker buttons */}
      </main>
    </div>
  );
}

export default GenerationView;
